import { useEffect, useRef, useState } from "react";
import { api } from "../api";
import { CpuIcon } from "../icons";
import type { ModelSpec, ProviderModels, ThinkingLevel } from "../types";
import { providerChoice, THINKING_LEVELS, thinkingChoice, thinkingLevelOf } from "./modelChoice";

// Fetched once per page: the catalog only changes when credentials do.
let catalog: Promise<ProviderModels[]> | undefined;
function loadCatalog(): Promise<ProviderModels[]> {
	catalog ??= api.models().then((r) => r.providers);
	return catalog;
}

/**
 * The model a session runs on, as one button beside the send button naming what is in effect,
 * or as an icon in the chat header on a phone.
 * It opens a small panel: a provider, then that provider's models, then the thinking level.
 * The first provider entry is the profile's own model (ADR-0045); anything else pins the session.
 */
export function ModelPicker({
	choice,
	running,
	thinkingLevel,
	disabled,
	onChange,
}: {
	/** The person's pin; null is the profile's model. */
	choice: ModelSpec | null;
	/** What the session is on right now. */
	running: ModelSpec;
	/** The level actually in effect, which the server clamps to what the model supports. */
	thinkingLevel: ThinkingLevel;
	disabled: boolean;
	onChange: (choice: ModelSpec | null) => void;
}) {
	const [providers, setProviders] = useState<ProviderModels[]>([]);
	const [open, setOpen] = useState(false);
	const root = useRef<HTMLSpanElement>(null);
	useEffect(() => {
		void loadCatalog().then(setProviders).catch(() => {});
	}, []);
	// A click anywhere else closes the panel, the way a menu is expected to behave.
	useEffect(() => {
		if (!open) return;
		const away = (e: MouseEvent) => {
			if (!root.current?.contains(e.target as Node)) setOpen(false);
		};
		document.addEventListener("mousedown", away);
		return () => document.removeEventListener("mousedown", away);
	}, [open]);

	const active = choice ?? running;
	const entry = providers.find((p) => p.provider === active.provider);
	// Unknown until the catalog arrives, and then only hidden when the model is known not to think.
	const thinks = entry?.models.find((m) => m.id === active.name)?.reasoning ?? true;

	return (
		<span className="model-picker" ref={root}>
			<button className="ghost" disabled={disabled} title="模型" onClick={() => setOpen((v) => !v)}>
				<CpuIcon size={18} className="model-glyph" />
				<span className="model-label">
					{active.name}
					{thinks && ` · ${thinkingLevel}`}
				</span>
			</button>
			{open && (
				<div className="model-menu">
					<label>
						<span>provider</span>
						<select value={choice?.provider ?? ""} onChange={(e) => onChange(providerChoice(e.target.value, providers, running, thinkingLevel))}>
							<option value="">默认</option>
							{providers.map((p) => (
								<option key={p.provider} value={p.provider}>
									{p.name}
									{p.subscription && "（订阅）"}
								</option>
							))}
						</select>
					</label>
					<label>
						<span>模型</span>
						<select value={active.name} disabled={!choice} onChange={(e) => onChange({ provider: active.provider, name: e.target.value, thinking: thinkingLevel })}>
							{choice && entry ? (
								entry.models.map((m) => (
									<option key={m.id} value={m.id}>
										{m.id}
									</option>
								))
							) : (
								<option value={active.name}>{active.name}</option>
							)}
						</select>
					</label>
					{thinks && (
						<label>
							<span>思考</span>
							<select value={thinkingLevel} onChange={(e) => onChange(thinkingChoice(choice, running, thinkingLevelOf(e.target.value)))}>
								{THINKING_LEVELS.map((level) => (
									<option key={level} value={level}>
										{level}
									</option>
								))}
							</select>
						</label>
					)}
				</div>
			)}
		</span>
	);
}
